<template>
<Txt class="mt-4 flex items-center gap-2 text-base font-medium">{{ $t('Company.Authentication') }}</Txt>
  <p class="text-sm text-gray-600">{{ $t('Company.AuthenticationDescription')}}</p>

  <CGBanner
    :title="$t('Company.AuthenticationMethod')"
    :subtitle="$t('Company.AuthenticationMethodDescription')"
    variant="purple"
  >
    <template #icon>
      <font-awesome-icon :icon="['fas','key']" class="text-purple-600 w-5 h-5" />
    </template>
  </CGBanner>

  <!-- Authentication type -->
  <div class="space-y-6 pt-10">
    <h3 class="text-lg font-semibold flex items-center space-x-2">
      <font-awesome-icon :icon="['fas','shield']" class="text-purple-800 w-5 h-5" />
      <span class="pt-1">{{ $t('Company.AuthenticationType') }}</span>
    </h3>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Traditional method, nothing to do -->
      <RadioCard
        :id="AuthenticationType.Credentials"
        v-model="draftCompany.authType"
        :title="$t('Company.Credentials')"
        :subtitle="$t('Company.CredentialsDescription')"
        variant="blue"
      >
        <template #icon>
          <font-awesome-icon :icon="['fas','key']" class="text-white w-8 h-8" />
        </template>
      </RadioCard>

      <!-- SSO, show configuration -->
      <RadioCard
        :id="AuthenticationType.SSO"
        v-model="draftCompany.authType"
        :title="$t('Company.SSO')"
        :subtitle="$t('Company.SSODescription')"
        variant="green"
      >
        <template #icon>
          <font-awesome-icon :icon="['fas','shield-halved']" class="text-white w-8 h-8" />
        </template>
      </RadioCard>

      <!-- Hybrid, show configuration-->
      <RadioCard
        :id="AuthenticationType.Hybrid"
        v-model="draftCompany.authType"
        :title="$t('Company.Hybrid')"
        :subtitle="$t('Company.HybridDescription')"
        variant="orange"
      >
        <template #icon>
          <font-awesome-icon :icon="['fas','bolt']" class="text-white w-8 h-8" />
        </template>
      </RadioCard>

      <!-- Skip config, show banner only -->
      <RadioCard
        id="Skip"
        v-model="draftCompany.authType"
        :title="$t('Company.Skip')"
        :subtitle="$t('Company.SkipDescription')"
      >
        <template #icon>
          <font-awesome-icon :icon="['fas','clock']" class="text-white w-8 h-8" />
        </template>
      </RadioCard>
    </div>

    <div v-if="[AuthenticationType.SSO, AuthenticationType.Hybrid].includes(draftCompany?.authType)" class="cyber-card p-6 bg-green-50 border-green-200">
      <h3 class="font-semibold text-green-900 mb-6 flex items-center space-x-2">
        <font-awesome-icon :icon="['fas','gear']" class="text-green-800 w-5 h-5" />
        <span>{{ $t('Company.SSOConfiguration') }}</span>
      </h3>
      <div class="space-y-6">
        <CGBanner
          :title="$t('Company.SSOEnabled')"
          :subtitle="$t('Company.SSOEnabledDescription')"
          variant="green"
        >
          <template #icon>
            <Icon name="info" size="24" class="text-green-600" />
          </template>
        </CGBanner>

        <div class="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
          <div class="space-y-1 w-full">
            <div class="flex justify-between items-center">
              <div class="flex flex-col text-left text-sm">
                <span class="font-medium">{{ $t('Company.UseMetadata') }}</span>
                {{ $t('Company.UseMetadataDescription') }}
              </div>
              <Toggle v-model="draftCompany.useMetadataXML" class="ml-4" @update:modelValue="cleanMetadata" />
            </div>
          </div>
        </div>
        <div v-if="!draftCompany.useMetadataXML" class="space-y-2">
          <FormGroup class="mt-4" html-for="customerMetadata" :label="$t('Company.CustomerMetadata')">
            <CGInput
              id="customerMetadata"
              v-model="draftCompany.samlDescriptor"
              :placeholder="$t('Company.CustomerMetadataPlaceholder')"
            ></CGInput>
          </FormGroup>
          <Txt class="mt-5 text-gray-700 text-xs">{{ $t('Company.CustomerMetadataDescription') }}</Txt>
        </div>
        <div v-if="draftCompany.useMetadataXML" class="space-y-2">
          <FormGroup class="mt-4" html-for="customerMetadata" :label="$t('Company.SSOServiceXML')">
            <CGInput
              id="metadataXml"
              v-model="selectedFileName"
              :placeholder="$t('General.NoFile')"
              :accept="acceptedFormats"
              @click="uploadFile(draftCompany.xmlFilename)">
              <template #suffix>
                <CGButton ref="fileUploadRef" @click="uploadFile(draftCompany.xmlFilename)" >
                  {{ $t('General.Upload') }}
                </CGButton>
              </template>
            </CGInput>
          </FormGroup>
          <FileUploadModal ref="fileUploadModal" :trigger="fileUploadRef" :file-label="draftCompany.xmlFilename" :max-size="maxFileSize" :accepted-formats="acceptedFormats" @submit="fileChanged" />
        </div>
      </div>

      <CGBanner
        :title="$t('Company.SSONote')"
        :subtitle="$t('Company.SSONoteDescription')"
        variant="blue"
      >
        <template #icon>
          <Icon name="info" size="24" class="text-blue-600" />
        </template>
      </CGBanner>
    </div>
  
    <!-- Show this banner when "Skip config" has been selected -->
    <CGBanner
      v-if="draftCompany.authType === 'Skip'"
      :title="$t('Company.SkipConfig')"
      :subtitle="$t('Company.SkipConfigDescription')"
      variant="gray"
    >
      <template #icon>
        <Icon name="info" size="24" class="text-gray-600" />
      </template>
    </CGBanner>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { AuthenticationType } from '@/common/constants';

export default defineComponent({
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      AuthenticationType,
      draftCompany: { ...this.company },
      fileUploadRef: null, // Reference for the file upload button
      fileUploadModal: null, // Reference for the file upload modal
      maxFileSize: 1048576, // 1 MB in bytes
      acceptedFormats: ['.xml'] // Accepted file formats
    };
  },
  watch: {
    company: {
      handler(newVal) {
        this.draftCompany = { ...newVal };
      },
      deep: true
    }
  },
  computed: {
    selectedFileName() {
      return this.draftCompany.metadataXML?.name || '';
    }
  },
  methods: {
    fileChanged(files) {
      if (!files || !files.fileList || files.fileList.length === 0) {
        this.draftCompany.metadataXML = null;
        return;
      }

      this.draftCompany.metadataXML = files.fileList[0];
    },
    uploadFile(filename) {
      this.draftCompany.xmlFilename = filename;
      this.$refs.fileUploadModal.showUploadModal(true);
    },
    cleanMetadata() {
      this.draftCompany.samlDescriptor = null; 
      this.draftCompany.metadataXML = null;
    }
  },
});
</script>